import { useCallback } from "react";
import {
  useNotesQuery,
  useUpsertNoteMutation,
  NotesFilter,
  NotesQuery,
  NotesDocument,
  UpsertNoteMutationVariables,
  Note,
} from "@/features/workspace/graphql/generated";

interface UpsertNoteInput {
  id?: number;
  type: string;
  text?: string;
  assessment_id?: number;
  person_id?: number;
  position_id?: number;
}

export const useAssessmentNotes = (assessmentId: number, personId: number) => {
  const { data, loading, refetch } = useNotesQuery({
    variables: {
      person_id: personId,
      filter: {
        assessment_id: assessmentId,
      } as NotesFilter,
    },
    skip: !assessmentId || !personId,
  });

  // Newly created notes must be written into the cached Notes list by hand —
  // updates propagate through Note:<id> normalization, but a create is invisible
  // to the query until a refetch. A broad refetchQueries here would reset the
  // note editors mid-typing (see d64662b79), so only append the created note.
  const [upsertNoteMutation, { loading: createNoteLoading }] =
    useUpsertNoteMutation({
      update: (cache, { data }) => {
        const upserted = data?.upsertNote;
        if (!upserted?.id) return;

        const variables = {
          person_id: personId,
          filter: { assessment_id: assessmentId } as NotesFilter,
        };

        const cached = cache.readQuery<NotesQuery>({
          query: NotesDocument,
          variables,
        });
        if (!cached?.notes) return;

        if (!cached.notes.some((note) => note?.id === upserted.id)) {
          cache.writeQuery({
            query: NotesDocument,
            variables,
            data: { notes: [...cached.notes, upserted] },
          });
        }
      },
    });

  const refetchNotes = () => {
    refetch();
  };

  const handleUpserteNote = useCallback(
    async (noteInput: UpsertNoteInput) => {
      const result = await upsertNoteMutation({
        variables: {
          ...noteInput,
          person_id: personId,
          assessment_id: noteInput.assessment_id || assessmentId,
          type: noteInput.type || "comment",
        } as UpsertNoteMutationVariables,
      });

      return result.data?.upsertNote;
    },
    [assessmentId, personId]
  );

  const notes = (data?.notes || []) as Note[];

  return {
    notes,
    loading,
    isSavingNote: createNoteLoading,
    handleUpserteNote,
    refetchNotes,
  };
};
